import type { PlacementWithLogical } from "@chakra-ui/react";
import { Button, Tag, Tooltip, useBreakpointValue } from "@chakra-ui/react";
import { useButtonProps, useUserProfile, useUserSubscriptionDetails, useUserSubscriptionDetailsDaysTillNextBilling } from "hooks";
import { getUsedQuantity } from "api/usage/models/FeatureUsage";
import React from "react";
import { useConversationContext } from "screens/thread/ConversationContext";
import { track } from "api/analytics";
import { USER_UPGRADE_PLAN_CLICKED } from "api/analytics/events";
import { useFeatureUsageWithFetch } from "hooks/useFeatureUsageWithFetch";
import { MaxioComponent } from "types/maxioComponents";
import { useLocation, useNavigate } from "react-router-dom";
import { useAddToCharliContext } from "screens/panels/addToCharli/AddToCharliWizard/AddToCharliProvider";

export interface Feature {
  name: string;
  trial: boolean | string;
  business: boolean | string;
}

export const UpgradePlanButton = ({
  buttonSize,
  label = "UPGRADE",
  placement = "auto",
  tooltip,
  style,
  isDisabled,
  onClick,
}: {
  buttonSize: string;
  label?: string;
  tooltip?: string;
  placement?: PlacementWithLogical;
  style?: any;
  isDisabled?: boolean;
  onClick?: () => void;
}) => {
  const buttonStyle = useButtonProps(buttonSize, "subscribe");
  const { isUpgrading } = useConversationContext();
  const { onSettingsClose } = useAddToCharliContext();
  const { email, fullName } = useUserProfile();
  const subscriptionInfo = useUserSubscriptionDetails();
  const daysTillPlanExpiry = useUserSubscriptionDetailsDaysTillNextBilling();
  const answerUsage = useFeatureUsageWithFetch(MaxioComponent.askQuestions);
  const projectUsage = useFeatureUsageWithFetch(MaxioComponent.createDueDiligenceProject);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false }) || false;

  const handleOnClick = () => {
    if (isDisabled || isUpgrading) {
      return;
    }

    track(USER_UPGRADE_PLAN_CLICKED, {
      pageViewed: pathname,
      userName: fullName,
      userEmail: email,
      answersUsed: answerUsage ? getUsedQuantity(answerUsage) : undefined,
      dueDiligenceUsed: projectUsage ? getUsedQuantity(projectUsage) : undefined,
      currentPlan: subscriptionInfo.plan,
      planStatus: subscriptionInfo.status,
      nextBillingDate: subscriptionInfo.nextBillingDate,
      subscriptionId: subscriptionInfo.subscriptionId,
      daysTillPlanExpiry: daysTillPlanExpiry.daysTillNextBilling,
    });

    if (onClick) {
      onClick();
    } else {
      navigate("/pricing");
      onSettingsClose();
    }
  };

  return (
    <Tooltip placement={placement} label={isMobile ? "" : tooltip} aria-label={tooltip} maxWidth="13rem">
      {buttonSize === "xxs" ? (
        <Tag
          {...buttonStyle}
          {...style}
          _hover={{
            transform: "scale(1.02)",
            transition: "all 0.2s ease-in-out",
          }}
          opacity={isDisabled || isUpgrading ? 0.4 : 1}
          pointerEvents={isDisabled || isUpgrading ? "none" : "auto"}
          borderRadius={"3px"}
          cursor={isDisabled || isUpgrading ? "not-allowed" : "pointer"}
          px="5px"
          py="1px"
          width="100%"
          textAlign={"center"}
          fontSize="11px"
          fontWeight={"semibold"}
          minHeight={"1.2rem"}
          onClick={handleOnClick}>
          {label}
        </Tag>
      ) : (
        <Button
          isDisabled={isDisabled || isUpgrading}
          {...buttonStyle}
          {...style}
          _hover={{
            transform: "scale(1.02)",
            transition: "all 0.2s ease-in-out",
          }}
          onClick={handleOnClick}>
          {label}
        </Button>
      )}
    </Tooltip>
  );
};
